/*
    File serves the problem set page
    data.status contains the actual data to be shown to the page (status) -> user status of problems
 */
import { useRef, useState, useCallback, useEffect } from 'react';
import { BuildCSV, handleFileUpload, sitesOptions } from '../../Utils/UtilityFunctions';
import Service from '../../Service/http';
import{ debounce } from 'lodash'
import { useMutation, useQuery } from 'react-query';
import { MdOutlineRemove } from 'react-icons/md';
import Util from '../../Service/util';
import { toast } from 'react-toastify'
import { FaDownload, FaEdit, FaFileUpload, FaLink, FaTag } from 'react-icons/fa';
import { GrPowerReset } from 'react-icons/gr';
import { BeatLoader } from 'react-spinners';
import { Link, useSearchParams } from 'react-router-dom';
import { HiSortAscending, HiSortDescending } from 'react-icons/hi'
import { handleDownload } from './ProblemsetUtil';
import Pagination from "@mui/material/Pagination";
import { Tooltip, Typography, Tabs, Tab, Badge } from '@mui/material';
import ProblemSetModal from './ProblemSetModal';
import RatingModal from './RatingModal';
import LoadingTableRows from './LoadingTableRows';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { tagOptions } from '../Reports/tagUtil';

const ProblemSet = () => {

    const [file, setFile] = useState([]) // state for filtering
    const [searchParams, setSearchParams] = useSearchParams();
    const [problemArray, setProblemArray] = useState([]) // state for uploading data to db
    const [filteredData, setFilteredData] = useState([])
    const [selectedTags, setSelectedTags] = useState([]);
    const [problemId, setProblemId] = useState('')
    const [userEmail, setUserEmail] = useState('')
    const [filter, setFilter] = useState('')
    const [allTags, setAllTags] = useState([])
    const [tags, setTags] = useState([])
    
    // search params state
    const [phase, setPhase] = useState(searchParams.get('phase') || 'all')
    const [siteName, setSiteName] = useState(searchParams.get('siteName') || 'all')
    const [selectedEditorial, setSelectedEditorial] = useState(searchParams.get('editorial') || 'all')
    const [minRating, setMinRating] = useState(searchParams.get('minRating') || 0);
    const [maxRating, setMaxRating] = useState(searchParams.get('maxRating') || 5000);
    const [urlTags, setUrlTags] = useState(searchParams.get('tags') || 'none')

    const [modalShow, setModalShow] = useState(false)
    const [showRatingModal, setShowRatingModal] = useState(false);
    const [showPopover, setShowPopover] = useState(false);  

    const [sortingOptions, setSortingOptions] = useState({problemName : true, rating : true});
    const service = new Service();
    const usernameInputRef = useRef(null)
    const util = new Util();
    const isAdmin = util.isAdmin();

    // pagination states
    const [page, setPage] = useState(Number(searchParams.get('page')) || 1)
    const [limit, setLimit] = useState(Number(searchParams.get('limit')) || 10)
    const limitArray = [10, 20, 50, 100, 200]

    const tableRef = useRef(null);

    const siteToProblemURLMap = {
        "CODECHEF":"https://codechef.com/problems",
        "CODEFORCES":"https://codeforces.com/problemset/problem",
        "LEETCODE":"https://leetcode.com/problems",
        // further sites url can be added here
    }

    const uploadFile = (e, setFile) => {
        handleFileUpload(e, setFile)
    };

    const { data, isLoading: loading, isFetching, refetch} = 
        useQuery(['problemset', file, page, limit, selectedEditorial, siteName, minRating, maxRating, phase, selectedTags], 
            () => {
            return service.post(
                `api/problemset/status?page=${page}&limit=${Math.min(limit, 200)}&siteName=${siteName}&editorial=${selectedEditorial}&minRating=${minRating}&maxRating=${maxRating}&phase=${phase}`, {email: userEmail, problemsArray: file, tags: selectedTags}
            )
        },  {
            refetchOnWindowFocus: false,
            onSuccess(json) {
                setFilteredData(json.status.sort((a, b) => b.solvedBy?.length - a.solvedBy?.length));
                setPage(Math.min(Number(json.currentPage), json.totalPages || 1))
            }
    })

    const deleteProblemFromProblemSet = ( id ) => {
        
        service.delete(`api/problemset/remove/${id}`).then(response => {
            refetch();
            toast.success("Problem Removed From Problemset");
        }).catch(error => {
            console.error(error)
            toast.error("Unable to Remove Problem");
        }) 
    }

    const getStatusOfProblemSet = async () => {

        const response = await service.post('api/problemset/update', {
            problemsArray: problemArray,
        });
        
        return response;
    }

    const { mutate, isLoading } = useMutation(getStatusOfProblemSet, {
        onSuccess(json) {
            toast.success(json.result)

            // download the report only if any problem failed to upload or data not added to that problem like tags
            if(json.status.length > 0){ 
                BuildCSV(
                    json.status, 
                    ['problemName', 'problemId', 'siteName', 'rating', "STATUS"], 
                    'ProblemSetUploadData'
                );
            }
        },
        onError(error) {
            toast.error("Error while uploading problemset");
            console.error(error);
        }
    })

    const handleTagsChange = ( tag ) => {

        if(selectedTags.includes(tag)) {
            const filteredTags = selectedTags.filter(includedTags => {
                return includedTags !== tag
            });

            setSelectedTags(filteredTags)
            if(filteredTags.length === 0) setUrlTags('none')
            else setUrlTags(filteredTags.join(','))
        
        } else {

            const newTags = [...selectedTags, tag]
            setSelectedTags(newTags);
            setUrlTags(newTags.join(','))

        }
    }

    // this function helps to filter the tags from the tag list
    const handleFilterTags = ( e ) => {

        setFilter(e.target.value)
        const filteredTags = tags.filter((tagObj) => {
            return tagObj.value.toLowerCase().includes(e.target.value.toLowerCase());
        });

        setAllTags(filteredTags)
    }
    
    const generateProblemsetReport = async () => {

        try {
            
            const users = userEmail.split(',')
            const response = await service.post('api/problemset/report', { users });
            handleDownload(response.data, 'ProblemsetReport.xlsx')
            
        } catch (error) {
            
            console.error(error);
            toast.error("Error in Generating Problemset Report", error);

        }

    }

    const rows = filteredData?.map((problem, index) => {

        const siteLink =  siteToProblemURLMap[problem.siteName]
        return (
            <>
                <tr 
                    key={problem._id} 
                    className="problem-row" 
                    style={{
                        backgroundColor: problem.solved ? 'rgba(167, 255, 191, 0.6)' : !problem.exist ? '#ffc3c3' : 'initial', 
                        fontWeight: '400',
                    }}
                >
                    <td>{((page-1) * Math.min(limit, 200)) + (index + 1)}</td>
                    <td>{problem.siteName ?? "N/A"}</td>
                    <td>{problem?.problemId ?? "N/A"}</td>
                    <td>
                        <a href={`${siteLink}/${problem.problemId}`} target='_blank' rel='noreferrer'>{problem.problemName}</a>
                    </td>
                    <td>{!problem.rating || problem.rating === 0 ? 'N/A' : problem.rating}</td>
                    <td>{problem?.solvedCount}</td>
                    <td>
                        <div className="dropdown">
                            <button
                                className="custom-button"
                                type="button"
                                id="dropdownMenuButton"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                                style={{background: 'transparent'}}
                                disabled={!problem?.originalTag?.length}
                            >
                            <FaTag color={problem?.originalTag?.length > 0 ? 'black' : 'lightgray'}/>
                            </button>
                            <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton" style={{borderRadius: 10}}>
                                <li>
                                    <div className='problem-tags d-flex flex-wrap'>
                                        {
                                            problem?.originalTag?.map((tag) => {
                                                return <span className="badge m-1 bg-dark">{tag}</span>
                                            })
                                        }
                                        {
                                            problem?.normalizedTag?.map((tag) => {
                                                return <span className="badge m-1 bg-primary">{tag.label}</span>
                                            })
                                        }
                                    </div>
                                </li>
                            </ul>
                    </div>
                    </td>
                    <td className={`${problem?.editorial?.length > 0 ? "editorial-link" : ""}`}>
                        <div className='d-flex flex-wrap align-items-center'>
                            {
                                problem?.editorial.length === 0 ? <FaLink color='red'/> :
                                    problem?.editorial?.map((editorialLink) => {
                                        return <a href={editorialLink} target='_blank' rel="noreferrer" className='mx-1'><FaLink color='green'/></a>
                                    })
                            }
                        </div>
                    </td>
                    {isAdmin && <td className='text-center'>
                        <Link to={`/admin/edit/problem/${problem?._id}`} target='_blank'>
                            <FaEdit color='black'/>
                        </Link>
                    </td>}
                    {isAdmin && (
                        <td className='text-end'>
                            <button
                                className='custom-button text-danger bg-transparent'
                                onClick={() => {
                                    setProblemId(problem._id);
                                    setModalShow(true);
                                }}
                            >
                                <MdOutlineRemove size={23} />
                            </button>
                        </td>
                    )}
                </tr>
                {isAdmin && problem.solvedBy?.length > 0 && (
                    <tr className="solved-by-row">
                        <td colSpan={10} className='text-primary'>
                            {problem?.solvedBy?.map((userEmail) => {
                                return '[' + userEmail + '] '
                            })}
                        </td>
                    </tr>
                )}
            </>
        );
    })
    
    const popover = (
        <Popover className='border' style={{width: 400}}>
            <Popover.Header className='bg-secondary' style={{border : 0}}>
                <input type="text" 
                    className='form-control' 
                    style={{height: 30, outline: 'none !important', border: 0}} 
                    placeholder='Search Tags'
                    onChange={(e) => handleFilterTags(e)}
                    value={filter}
                />
            </Popover.Header>
            <Popover.Body>
                {selectedTags.length > 0 && <span>Selected Tags</span>}
                <div className='overflow-auto d-flex flex-wrap' style={{maxHeight: 100}}>
                {
                    selectedTags.map((tag) => {

                        return <button 
                        className={`custom-button bg-primary m-1 order-0 py-2 d-flex align-items-center text-white`} 
                        style={{height: '25px', color: 'black', fontWeight:'bold'}}
                        onClick={() => handleTagsChange(tag)}
                        >{tag}</button>
                    })
                }
                </div>
                <div className='overflow-auto' style={{height: 250}}>
                {
                    allTags.map((tag) => {

                        const includes = selectedTags.includes(tag.value);
                        return <button 
                        className={`d-${includes ? 'none' : 'block'} custom-button bg-${includes ? 'primary' : 'secondary'} m-1 order-0 py-2 d-flex align-items-center text-${includes ? 'light' : 'dark'}`} 
                        style={{height: '25px', color: 'black', fontWeight:'bold'}}
                        onClick={() => handleTagsChange(tag.value)}
                        >{tag.label}</button>
                    })
                }
                </div>
                <div className='bg-dark rounded' style={{height: '5px'}}></div>
                <div className='d-flex justify-content-end mt-2'>
                    <button 
                        className="custom-button text-dark bg-transparent"
                        onClick={() =>{ 
                            setSelectedTags([])
                            searchParams.set('tags', 'none');
                            setSearchParams(searchParams)
                            setUrlTags('none')
                        }}
                    >
                        <GrPowerReset/> {" "} Reset
                    </button>
                    <button 
                        className="custom-button text-white bg-danger" 
                        style={{fontWeight: 'bold'}}
                        onClick={() => setShowPopover(false)}>
                        Close
                    </button>
                </div>
            </Popover.Body>
        </Popover>
    )

    const debouncedFetchStatus = useCallback(
      debounce(() => {
        refetch()
      },1000), // Adjust the debounce delay as needed
      []
    );

    const handleDebounce = (value) => {
        setUserEmail(value)
        debouncedFetchStatus();
    };    
    
    const handleSortingByProblemName = () => {
        setFilteredData(prev => {
            return prev.sort((a, b) => {
                return sortingOptions.problemName ? a.problemName.toLowerCase().localeCompare(b.problemName.toLowerCase()) : b.problemName.toLowerCase().localeCompare(a.problemName.toLowerCase())
            })
        });

        setSortingOptions({...sortingOptions, problemName: !sortingOptions.problemName});
    }

    const handleSortingByRating = () => {
        setFilteredData(prev => {
            let result = 0;
            return prev.sort((a, b) => {
                
                const getRating = (item) => (item.rating == null ? (sortingOptions.rating ? Number.MAX_VALUE : Number.MIN_VALUE) : item.rating);

            // Sort by rating
            if (getRating(a) !== getRating(b)) {
                result = sortingOptions.rating ? getRating(a) - getRating(b) : getRating(b) - getRating(a);
            }
            return result;
                
            })
        });

        setSortingOptions({...sortingOptions, rating : !sortingOptions.rating})
    }

    const handleSortingBySolvedCount = () => {
        setFilteredData(prev => {
            let result = 0;
            return prev.sort((a, b) => {
    
                const getSolvedCount = (item) => (item.solvedCount == null ? (sortingOptions.solvedCount ? Number.MAX_VALUE : Number.MIN_VALUE) : item.solvedCount);
    
                // Sort by solved count
                if (getSolvedCount(a) !== getSolvedCount(b)) {
                    result = sortingOptions.solvedCount ? getSolvedCount(a) - getSolvedCount(b) : getSolvedCount(b) - getSolvedCount(a);
                }
                return result;
    
            })
        });
    
        setSortingOptions({...sortingOptions, solvedCount: !sortingOptions.solvedCount});
    }
    
    useEffect(() => {
    if(problemArray.length > 0) mutate()
    }, [problemArray.length, mutate, file.length]);

    useEffect(() => {
        setSearchParams({
            siteName: siteName, 
            editorial: selectedEditorial, 
            page, 
            limit : Math.min(limit, 200),
            minRating : minRating,
            maxRating : maxRating,
            phase, 
            tags : urlTags
        });
    }, [siteName, selectedEditorial, page, limit, setSearchParams, maxRating, minRating, phase, urlTags])

    useEffect(() => {
        if(urlTags !== 'none') {
            const includedTags = urlTags.split(',')
            setSelectedTags(includedTags);
        }
    }, [urlTags])

    useEffect(() => {
        const fetchAllTags = async () => {
            const options = await tagOptions();
            setAllTags(options)
            setTags(options);
        }
        fetchAllTags()
    }, [])

    return (
       <div className="container-fluid my-2" style={{minHeight: '90vh'}}>

        {/* Modals */}

        <ProblemSetModal show={modalShow} onHide={() => setModalShow(false)} deleteProblemFromProblemSet={deleteProblemFromProblemSet} problemId={problemId}/>
        <RatingModal show={showRatingModal} onHide={() => setShowRatingModal(false)} minRating={minRating} setMinRating={setMinRating} maxRating={maxRating} setMaxRating={setMaxRating}/>

        {/* problemset page header */}
            {
                isAdmin && 
                <div className="problemset-header d-flex my-2 align-items-center justify-content-end rounded" style={{padding: 7}}>
                    <div className='d-flex justify-content-between align-items-center w-100'>

                        <div className='filter-problem-div'>

                            {
                                !file.length && <Link
                                className="mb-2 custom-button border text-primary"
                                to="/files/Sample_FilterProblemSetProblems.xlsx"
                                target="_blank"
                                download
                                title='Download Sample For Filtering'
                                
                            >
                                <FaDownload />
                
                            </Link>
                            }
                            {"  "}
                            { !file.length && 
                                <label htmlFor="upload-problems-file" style={{cursor: 'pointer', color: '#1688b4'}} className='custom-button border'>
                                    <FaFileUpload size={20}/> {" "} Filter Problems
                                    <input type="file" id="upload-problems-file" className="d-none" onChange={(e) => uploadFile(e, setFile)}/>
                                </label>
                            }
                            {
                            file.length > 0 && <button 
                            className='custom-button mx-2'
                            onClick={() => {
                                setFile([]);
                            }}
                            >
                            <GrPowerReset/>
                            </button>
                            }
                        </div>

                        <input type="text" className="form-control w-50 mx-3" placeholder="Search By User Emails" onChange={(e) => handleDebounce(e.target.value)} ref={usernameInputRef}/>
                        <div className='upload-problemset'>
                                <Link
                                    className="mb-2 custom-button border text-primary"
                                    to="/files/SampleProblemSetData.xlsx"
                                    target="_blank"
                                    download
                                    title='Download Sample For Problemset'
                                >
                                    <FaDownload />

                                </Link>
                            {"  "}
                            { !problemArray.length && 
                                <label htmlFor="upload-problemset-file" style={{cursor: 'pointer', color: '#1688b4'}} className='custom-button border'>
                                    <FaFileUpload size={20}/> {" "} Upload Problem Set
                                </label>
                            }

                            {
                                problemArray.length > 0 && <button 
                                className='custom-button mx-2'
                                onClick={() => {
                                    setProblemArray([]);
                                }}
                                >
                                    <GrPowerReset/>
                                </button>
                            }
                                <input type="file" id="upload-problemset-file" className="d-none" onChange={(e) => uploadFile(e, setProblemArray)}/>
                                    </div>
                                </div>
                </div>
            }
        
        {/*  UserName and Problemset Report section */}
            <div className='container-fluid d-flex justify-content-between my-1' style={{padding: '7px 0px', height: '50px'}}>
                {
                    data && <span className='text-primary' style={{fontWeight: 'bold'}}>{data?.username}{data?.currentUser && isAdmin && <span style={{fontWeight: 'bold'}}>{" ( You )"}</span>}</span>
                } 
                {(isFetching || isLoading) && <BeatLoader size={5} color='rgb(10, 120, 168)'/>}
                {
                    data && isAdmin && <button onClick={generateProblemsetReport} className='custom-button text-dark'>Problem Set Report<span className='mx-2' title='Download Problemset Report'><FaDownload/></span></button>
                }
            </div>
        
        {/* Phase Tabs */}
        <div className='tab-container d-flex justify-content-between border overflow-auto rounded'>
            <Tabs onChange={(e, val) => setPhase(val)} value={phase} variant='scrollable' sx={{width: '100%'}}>
                <Tab label={"All Phases"} value={"all"} sx={{width: '25%'}}/>
                <Tab label={"Phase 1"} value={"phase-1"} sx={{width: '25%'}}/>
                <Tab label={"Phase 2"} value={"phase-2"} sx={{width: '25%'}}/>
                <Tab label={"Phase 3"} value={"phase-3"} sx={{width: '25%'}}/>
            </Tabs> 
        </div>

        {/* problemset page body */}
             <div className="problemset-body overflow-auto">
                {
                 <table className='table table-bordered mt-3' ref={tableRef}>
                    <thead>
                        <tr className="text-center">
                            <th>#</th>
                            <th id='site-header'>
                                <select 
                                    name="" 
                                    id="" 
                                    className='form-control' 
                                    value={siteName} onChange={(e) => {
                                        setSiteName(e.target.value)                                    
                                    }}
                                    style={{fontWeight: 'bold'}}
                                    >
                                    <option value="all">All-Sites</option>
                                    {
                                        sitesOptions.map((siteName) => {
                                            return <option value={siteName}>{siteName}</option>
                                        })
                                    }
                                </select>
                            </th>
                            <th>Problem Id</th>
                            <th>
                                <div className='d-flex justify-content-between'>
                                    <span>Name</span>
                                    <button onClick={handleSortingByProblemName} className='custom-button text-dark'>
                                        {sortingOptions.problemName ? <HiSortAscending/> : <HiSortDescending/>}
                                    </button>
                                </div>
                            </th>
                            <th>
                            <div className='d-flex justify-content-between'>
                                    <Tooltip title={<Typography>Click To Filter Problems By Rating</Typography>} placement='top' arrow>
                                        <button className='custom-button text-dark bg-transparent' style={{fontWeight: 'bold'}} onClick={() => {
                                            setShowRatingModal(true)
                                        }}>Rating</button>
                                    </Tooltip>
                                    <button onClick={handleSortingByRating} className='custom-button text-dark'>
                                        {sortingOptions.rating ? <HiSortAscending/> : <HiSortDescending/>}
                                    </button>
                                </div>
                            </th>
                            <th>
                            <div className='d-flex justify-content-between'>
                                    <span>Solved Count</span>
                                    <button onClick={handleSortingBySolvedCount} className='custom-button text-dark'>
                                        {sortingOptions.rating ? <HiSortAscending/> : <HiSortDescending/>}
                                    </button>
                                </div>
                            </th>
                            <th>
                                <OverlayTrigger 
                                    overlay={popover} 
                                    trigger={'click'} 
                                    placement='bottom' 
                                    show={showPopover}
                                    >
                                    <button className='bg-transparent custom-button text-black' style={{fontWeight: 'bold'}} onClick={() => setShowPopover(prev => !prev)}>
                                        <Tooltip title={<Typography>Select Tags</Typography>} arrow placement='top'>
                                            <Badge badgeContent={selectedTags.length} color='info'>
                                                <span style={{padding: 2}}>Tags</span>
                                            </Badge>
                                        </Tooltip>
                                    </button>
                                </OverlayTrigger>
                            </th>
                            <th className='text-center'>
                                <select name="editorial" id="editorial" className='form-control' onChange={(e) => setSelectedEditorial(e.target.value)} style={{fontWeight: 'bold'}} value={selectedEditorial}>
                                    <option value="all">Editorial</option>
                                    <option value={'available'} className='text-success'> ✔️ Available</option>
                                    <option value={'not-available'} className='text-danger'>❌ Not Available</option>
                                </select>
                            </th>
                            {isAdmin && <th>Edit</th>}
                            {isAdmin && <th>Remove</th>}
                        </tr>
                    </thead>

                    <tbody>
                        
                        {
                            loading ? 
                                <LoadingTableRows isAdmin={isAdmin}/>
                             : rows
                        }

                    </tbody>
                </table>
                }

                <div className="container-fluid d-flex justify-content-center my-4">
                    <Pagination
                        count={data?.totalPages}
                        color="primary"
                        defaultPage={1}
                        page={page}
                        onChange={(e, v) => {
                            setPage(v);
                        }}
                        variant="outlined"
                        shape="rounded"
                    />
                    <select 
                        className="custom-select" 
                        style={{width: '4rem', height: '2rem', borderRadius: '5px', border: '1px solid gray', outline: 'none', textAlign: 'center', margin: '0px 20px'}}
                        onChange={(e) => {
                            setLimit(e.target.value)
                            if(data?.totalPages < page) setPage(1)
                        }}
                        value={limit}
                    >
                        {
                            limitArray.map((limits) => {
                                return <option value={limits}>{limits}</option>
                            })
                        }
                        
                </select>
                </div>
            </div>
       </div>
    )
}

export default ProblemSet